import {arNum, cueTime, STATUS_LABEL} from '../format';
import {useStudio} from '../store';
import type {Aspect, Scene} from '../types';

import {Editable} from './Editable';
import {Skeleton} from './Elapsed';
import {Chevron, Regenerate, Trash} from './Icons';
import {VisualColumn} from './VisualColumn';
import {VoiceoverBlock} from './VoiceoverBlock';

/**
 * One scene, with every element individually reviewable, editable and regenerable —
 * which is the requirement this whole screen exists to satisfy.
 */
export function SceneCard({scene, aspect}: {scene: Scene; aspect: Aspect}) {
  const s = useStudio();
  const view = s.sceneView(scene);
  const busy = s.isBusy(`scene:${scene.id}`);
  const error = s.errorFor(`scene:${scene.id}`);
  const cuesOpen = !!s.cuesOpen[scene.id];

  const regenerate = () => void s.regenerateScene(scene.id);

  // Where this scene sits on the timeline. The card only receives its own scene,
  // so the offset comes from the ones before it in the store's ordering.
  const all = s.scenes();
  const index = all.findIndex((sc) => sc.id === scene.id);
  const startsAt = all.slice(0, Math.max(index, 0)).reduce((sum, sc) => sum + sc.duration_s, 0);

  // The card border and header tint carry the scene's own state, so three scenes
  // in three different states read as three different states at a glance.
  const tone = view.status === 'generating' ? ' busy' : view.status === 'failed' ? ' fail' : '';

  return (
    <div
      className={`scene${tone}${s.focusedScene === scene.id ? ' on' : ''}`}
      id={`scene-${scene.id}`}
    >
      <div className="scene-head">
        <b>{String(scene.order).padStart(2, '0')}</b>
        <span className="scene-title">مشهد {arNum(scene.order)}</span>
        <i dir="ltr">
          {cueTime(startsAt)} → {cueTime(startsAt + scene.duration_s)}
        </i>
        <span className={`chip ${view.status}`}>{STATUS_LABEL[view.status]}</span>
        <span className="grow" />
        <div style={{display: 'flex', gap: 5}}>
          <button type="button" className="btn-out xs" disabled={busy} onClick={regenerate}>
            {busy ? <span className="spin" style={{width: 11, height: 11}} /> : <Regenerate />}
            أعد توليد المشهد
          </button>
          <button
            type="button"
            className="icon-round danger"
            title="حذف المشهد"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`حذف المشهد ${scene.order}؟ لا يمكن التراجع.`)) {
                void s.removeScene(scene.id);
              }
            }}
          >
            <Trash />
          </button>
        </div>
      </div>

      <div className="scene-body">
        <div className="scene-text">
          <div>
            <div className="blk-head" style={{marginBottom: 6}}>
              <span className="field-lbl">نص السيناريو</span>
              <button type="button" className="btn-txt" disabled={busy} onClick={regenerate}>
                <Regenerate />
                أعد التوليد
              </button>
            </div>
            {busy ? (
              <div style={{display: 'flex', flexDirection: 'column', gap: 9}}>
                <Skeleton />
                <Skeleton width="88%" />
                <Skeleton width="62%" />
              </div>
            ) : (
              <Editable
                className="script"
                value={scene.script_text}
                onCommit={(next) => void s.editScene(scene.id, 'script_text', next)}
              />
            )}
            {error && (
              <div className="err-note" style={{marginTop: 8}}>
                {error}
              </div>
            )}
          </div>

          <div className="two">
            <div>
              <div className="field-lbl" style={{marginBottom: 6}}>
                نوع اللقطة
              </div>
              <Editable
                className="boxed"
                value={scene.shot_type}
                onCommit={(next) => void s.editScene(scene.id, 'shot_type', next)}
              />
            </div>
            <div>
              <div className="field-lbl" style={{marginBottom: 6}}>
                المعالجة البصرية
              </div>
              <Editable
                className="boxed"
                value={scene.visual_treatment}
                onCommit={(next) => void s.editScene(scene.id, 'visual_treatment', next)}
              />
            </div>
          </div>

          <VoiceoverBlock scene={scene} view={view} />

          <div className="blk">
            <div className="blk-head">
              <span className="field-lbl">النص على الشاشة</span>
              <span className="h-note" style={{fontSize: 11.5}}>
                يظهر بالخط الفعلي للفيديو
              </span>
            </div>
            {/* Amiri here is the same face the ffmpeg overlay renders with, so what is
                edited is what appears in the video. */}
            <Editable
              className="osc"
              value={scene.on_screen_copy}
              onCommit={(next) => void s.editScene(scene.id, 'on_screen_copy', next)}
            />
          </div>

          <div className="blk" style={{paddingTop: 12}}>
            <button
              type="button"
              className={`cues-btn${cuesOpen ? ' open' : ''}`}
              disabled={scene.subtitle_cues.length === 0}
              onClick={() => s.toggleCues(scene.id)}
            >
              <Chevron />
              مقاطع الترجمة{' '}
              <span style={{color: 'var(--muted)', fontWeight: 400}}>
                ({arNum(scene.subtitle_cues.length)})
              </span>
            </button>
            {cuesOpen && scene.subtitle_cues.length > 0 && (
              <div className="cues">
                {scene.subtitle_cues.map((cue, i) => (
                  <div className="cue" key={`${cue.start}-${i}`}>
                    <span dir="ltr">
                      {cueTime(cue.start)} – {cueTime(cue.end)}
                    </span>
                    <span>{cue.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <VisualColumn scene={scene} view={view} aspect={aspect} />
      </div>
    </div>
  );
}
